import { BreadcrumbJsonLd, ProductJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import { getOfferImageLink } from 'lib/image';
import { toCategoryLink, toOfferLink } from 'lib/links';
import { toFormattedOfferOldPrice, toFormattedOfferPrice } from 'lib/offer';
import {
  appendDynamicSubcategoryToBreadcrumbSchema,
  toReviewSchema,
} from 'lib/schema';
import { toFormattedJsonLdString } from 'lib/string';

import APP from 'constants/app';
import OFFER from 'constants/offer';

const getAverageRating = (reviews) => {
  const totalStars = reviews.reduce(
    (acc, review) =>
      review.commentStars
        ? acc + Number(review.commentStars)
        : acc + OFFER.REVIEWS.DEFAULT_RATING_VALUE,
    0
  );
  const average = totalStars / reviews.length;

  return average;
};

const OfferSchema = ({
  offerId,
  offerTitle,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerOldPrice,
  offerSlug,
  categoryName,
  categorySlug,
  offerInstructionsRaw,
  reviews,
  storeDomain,
  storeName,
  subcategoryName,
  subcategorySlug,
}) => {
  const validSchemaReviews = toReviewSchema(
    reviews.filter(({ commentText }) => commentText),
    offerTitle
  );

  return (
    <>
      <BreadcrumbJsonLd
        itemListElements={appendDynamicSubcategoryToBreadcrumbSchema(
          [
            {
              position: 1,
              name: APP.BREADCRUMB_FIRST_ITEM,
              item: APP.URL,
            },
            {
              position: 2,
              name: categoryName,
              item: toCategoryLink(categorySlug),
            },
            {
              position: 3,
              name: toFormattedJsonLdString(offerTitle),
              item: `${APP.URL}${toOfferLink(offerSlug)}`,
            },
          ],
          subcategoryName,
          subcategorySlug
        )}
      />
      <ProductJsonLd
        productName={toFormattedJsonLdString(offerTitle)}
        images={[getOfferImageLink(offerPhoto, 268)]}
        description={offerInstructionsRaw}
        sku={offerId}
        reviews={validSchemaReviews}
        aggregateRating={
          validSchemaReviews.length > 0
            ? {
                ratingValue: getAverageRating(reviews),
                reviewCount: validSchemaReviews.length,
              }
            : null
        }
        offers={[
          {
            price: offerPrice,
            priceCurrency: 'BRL',
            availability: 'InStock',
            itemCondition: 'NewCondition',
            url: storeDomain,
            sellerName: storeName,
            offerCount: 1,
            lowPrice: toFormattedOfferPrice(offerPrice, offerPriceType),
            highPrice: toFormattedOfferOldPrice(offerOldPrice, offerPriceType),
            category: categoryName,
            seller: { name: storeName },
          },
        ]}
      />
    </>
  );
};

OfferSchema.defaultProps = {
  offerId: null,
  offerTitle: null,
  offerPhoto: null,
  offerPrice: null,
  offerOldPrice: null,
  categoryName: null,
  storeDomain: null,
  offerInstructionsRaw: null,
  offerSlug: null,
  storeName: null,
  subcategoryName: null,
  subcategorySlug: null,
};

OfferSchema.propTypes = {
  offerId: PropTypes.number,
  offerTitle: PropTypes.string,
  offerPhoto: PropTypes.string,
  offerPrice: PropTypes.number,
  offerOldPrice: PropTypes.number,
  categoryName: PropTypes.string,
  storeDomain: PropTypes.string,
  offerInstructionsRaw: PropTypes.string,
  offerSlug: PropTypes.string,
  storeName: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
};

export default OfferSchema;
